import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from './store';
import { Middleware } from '@reduxjs/toolkit';
import { AppConfig } from '../Config';
import { getCurrentOrganization } from '../common/selectors/auth';
import { authMutex, getAuthIdToken, recoverFromAuthFailure } from '../common/utils/authFetch';
import { getUserItem } from '../common/utils/userStorage';
import userContextSlice from '../common/slices/userContextSlice';

const fallbackApiUrl =
  AppConfig.api.apiUrl ||
  (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost');

const baseQuery = fetchBaseQuery({
  // Tests run in Node (jsdom) and may not provide a configured API URL.
  // Fall back to a safe absolute URL so fetchBaseQuery's Request() doesn't throw
  baseUrl: fallbackApiUrl,
  credentials: 'include', // Include credentials in cross-origin requests
  prepareHeaders: async (headers, { getState, endpoint }) => {
    // Bypass headers for certain endpoints
    if (endpoint === 'apiPortalV1FeaturesList') {
      return headers;
    }

    const state = getState() as RootState;

    // Source of truth for the auth token is Amplify, not localStorage.
    // getAuthIdToken() returns the cached token when valid and auto-refreshes
    // via the refresh token when the access/id token has expired. Hitting localStorage
    // directly meant we used a stale token until the next 401 forced a refresh,
    // which produced one failing request per access-token TTL (~1h).
    const token = await getAuthIdToken();
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    // Get organization ID from the current organization or fall back to localStorage / state
    // to prevent race conditions where organization data isn't loaded yet
    const currentOrg = getCurrentOrganization(state);
    let organizationId = currentOrg?.id;

    // Fallbacks: previously-selected org stored in localStorage or currentOrganization from state
    if (!organizationId) {
      const selectedOrgId = getUserItem('selected_org_id');
      if (selectedOrgId) {
        organizationId = Number(selectedOrgId);
      } else if ((state as RootState).userContext?.currentOrganization?.id) {
        organizationId = (state as RootState).userContext.currentOrganization?.id;
      }
    }

    if (organizationId) {
      headers.set('X-NULOGIC-ORGANIZATION-ID', organizationId.toString());
    }

    return headers;
  },
});

/**
 * Custom baseQuery wrapper that handles token refresh on 401 errors.
 * Refresh + signOut logic is shared with `authenticatedFetch` via the
 * `recoverFromAuthFailure` helper and `authMutex` in `common/utils/authFetch.ts`,
 * so concurrent 401s from RTK Query and direct fetch sites collapse into one
 * refresh attempt.
 */
const baseQueryWithTokenRefresh: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Wait for any ongoing token refresh to complete
  await authMutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  // Check if we have an authentication error (401 status or token revocation message)
  const isAuthError =
    result.error &&
    (result.error.status === 401 ||
      (typeof result.error.data === 'string' &&
        (result.error.data.includes('NotAuthorizedException') ||
          result.error.data.includes('Access Token has been revoked'))));

  if (isAuthError) {
    const authErrorMessage = result.error
      ? ((result.error.data as any)?.detail ||
         (result.error.data as any)?.message ||
         (typeof result.error.data === 'string' ? result.error.data : null))
      : null;

    const isUserBlockedError =
      authErrorMessage &&
      typeof authErrorMessage === 'string' && (
        authErrorMessage.includes('no longer active') ||
        authErrorMessage.includes('do not have access to any organizations')
      );

    if (isUserBlockedError) {
      console.error('RTK Query: User blocked error detected:', authErrorMessage);
      api.dispatch(userContextSlice.actions.setAuthenticationError(authErrorMessage));
      return result;
    }

    // In test mode, don't attempt token refresh - just return the error.
    // Test mode uses static JWT tokens that don't need Cognito refresh.
    // Defense-in-depth: also gate on build mode. REACT_APP_TEST_MODE is read
    // from process.env at build time and could be inherited from a CI runner;
    // requiring non-production mode prevents that leak from disabling the
    // signOut-on-refresh-failure behavior in prod.
    const isTestMode =
      process.env.REACT_APP_TEST_MODE === 'true' &&
      import.meta.env.MODE !== 'production';
    if (isTestMode) {
      console.log('RTK Query: Test mode detected, skipping token refresh for 401');
      return result;
    }

    console.warn('RTK Query: Authentication error detected, attempting token refresh');
    const retried = await recoverFromAuthFailure(async () => baseQuery(args, api, extraOptions));
    if (retried) {
      result = retried;
    }
  }

  return result;
};

export const emptySplitApi = createApi({
  reducerPath: 'emptySplitApi',
  baseQuery: baseQueryWithTokenRefresh,
  endpoints: () => ({}),
  tagTypes: [
    'ActionPlan',
    'ActionPlanActivity',
    'Auth',
    'Employee',
    'SurveyQuestion',
    'SurveyStructure',
    'SurveyResponse',
    'EmailDeliveryStatus',
    'Department',
    'Team',
    'OrganizationCategory',
    'OrganizationCategoryList',
    'Feature',
    'Account',
    'TaskManagement',
    'PerformanceMetric',
    'TeamCommitment',
    'TeamBio',
    'ProvenanceRecord',
    'repository-files',
    'file-uploads',
  ],
});

export const rtkQueryLogger: Middleware = store => next => (action: any) => {
  if (action.type.endsWith('/pending')) {
    console.log('RTK Query Request:', {
      endpoint: action.meta.arg.endpointName,
      url: action.meta.baseQueryMeta?.request?.url,
      method: action.meta.baseQueryMeta?.request?.method,
      body: action.meta.arg.originalArgs,
    });
  }

  return next(action);
};
